/**
 * English UI catalogue of the History panel and the Versions dialog (W2-10 projects-data; namespace 'history').
 * Imports only src/i18n/define.ts (UI message catalogue zone). ICU-lite syntax (src/i18n/icu.ts).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('history', {
  // ---- panel ----
  panelTitle: 'History',
  listLabel: 'Changes, newest first',
  summary: '{count, plural, one {# step} other {# steps}} · {memory} · kept after reload (the last 500 changes)',
  jumpToChange: 'Jump to the change on undo and redo',
  versionsButton: 'Versions...',
  truncated: 'Earlier changes cannot be undone here.',
  seeVersions: 'See Versions',
  openedDivider: 'Opened in this session above, earlier sessions below',
  startRow: 'Before these changes',
  noEffect: 'no visible change',
  currentSr: '(current state)',
  undoneSr: '(undone, can be redone)',
  justNow: 'just now',
  emptyTitle: 'No changes yet',
  emptyBody: 'Every edit appears here. Click an entry to go back to it; later entries stay until your next edit.',
  noProjectTitle: 'No chart open',
  noProjectBody: 'Open a chart to see its history.',
  gestureActive: 'Finish the current drag first.',
  hint: '{undo} undoes, {redo} redoes. Click an entry to go back or forward to it.',
  undoButton: 'The Undo button',
  redoButton: 'the Redo button',

  // ---- sizes ----
  sizeB: '{size} B',
  sizeKB: '{size} KB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',
  sizeTB: '{size} TB',

  // ---- versions dialog ----
  versionsTitle: 'Version history of {name}',
  retention:
    'Phiedit keeps every version from the last hour, one per hour for 2 days and one per day for 30 days, plus every checkpoint and export. Versions are made every 10 minutes of editing and before imports, exports, restores and large generator runs.',
  versionsListLabel: 'Versions, newest first',
  untitled: 'Untitled chart',
  checkpointName: 'Checkpoint name (optional)',
  checkpointPlaceholder: 'For example: before the drop rework',
  createCheckpoint: 'Create checkpoint',
  checkpointCreated: 'Checkpoint created.',
  checkpointNamed: 'Checkpoint "{name}" created.',
  notOpenNote: 'This chart is not open. Restoring a version opens it first.',
  budgetLabel: 'Versions use {used} of the {budget} kept for this chart',
  budgetNote:
    'Above the budget, the oldest automatic versions are removed first. Checkpoints and exports are always kept.',
  loading: 'Loading versions...',
  loadFailed: 'The versions could not be listed: {reason}',
  retry: 'Try again',
  noVersionsTitle: 'No versions yet',
  noVersionsBody: 'Phiedit makes one every 10 minutes of editing. {keys} creates a checkpoint now.',
  noVersionsBodyNoKeys:
    'Phiedit makes one every 10 minutes of editing. Create checkpoint (in the project menu) makes one now.',
  kindAuto: 'Automatic version',
  kindCheckpoint: 'Checkpoint',
  kindImport: 'Before import',
  kindExport: 'Exported',
  kindBeforeRestore: 'Before restore',
  kindBeforeGenerator: 'Before a generator',
  alwaysKept: 'always kept',
  currentState: 'current state',
  alreadyCurrent: 'This version is the current state.',
  restore: 'Restore',
  restoreLabel: 'Restore {name} from {when}',
  openCopy: 'Open as copy',
  openCopyLabel: 'Open as copy: {name} from {when}',
  working: 'Working on it...',
  close: 'Close',
  restored: 'Restored "{name}" from {when}. Undo brings your work back.',
  restoreTxn: 'Restore version: {label}',
  restoreNotOpen: 'Open the chart to restore one of its versions.',
  restoreGesture: 'Finish the current drag before restoring a version.',
  copyName: '{name} ({when})',
  copyCreated: 'Created "{name}" from the version.',
  lockedElsewhere: '"{name}" is open in another tab. Restore the version there, or close it there first.',
  lockedTitle: '"{name}" is open in another tab',
  lockedBody:
    'Only one tab can edit a chart at a time, so changes from two tabs never mix. Restore the version in that tab, or edit the chart here instead.',
  lockedSwitch: 'Switch to it',
  lockedSwitchHelp: 'Keep editing in the other tab and restore the version there.',
  lockedHere: 'Edit here instead',
  lockedHereHelp:
    'The other tab saves the chart, closes it and goes back to its Library. Then the chart opens here and the version is restored.',
  lockedSwitched:
    'Phiedit asked the other tab to come forward. If your browser did not switch, pick the Phiedit tab with "{name}" in the tab bar.',
  lockedRefused: 'The other tab did not hand the chart over. Close it there, then try again.',

  // ---- naming a version ----
  nameButton: 'Name',
  nameButtonLabel: 'Name {name} from {when}',
  renameButton: 'Rename',
  renameButtonLabel: 'Rename {name} from {when}',
  nameField: 'Name of this version',
  nameJustMade: 'Name the checkpoint you just made',
  nameSave: 'Save name',
  nameCancel: 'Cancel',
  nameEmpty: 'Type a name first.',
  named: 'Checkpoint named "{name}".',
  nameNoLonger:
    'The chart changed after that checkpoint, so it can no longer be named. Give the current state a name with Create checkpoint instead.',
  nameNotOpen: 'Open the chart to name its checkpoints.',
  actionFailed: 'That did not work: {reason}',

  // ---- commands ----
  cmdCheckpoint: 'Create checkpoint',
  cmdVersions: 'Version history...',
  cmdShow: 'Show history',
  cmdJumpToChange: 'Jump to the change on undo and redo',
  cmdClear: 'Clear undo history...',
  checkpointToast: 'All changes are saved automatically. Checkpoint created.',
  checkpointFailed: 'The checkpoint could not be created: {reason}',
  nameIt: 'Name it',
  cmdNameCheckpoint: 'Name checkpoint...',
  reasonNoProject: 'Open a chart first.',
  reasonNothingToClear: 'The history is empty.',
  clearTitle: 'Clear the undo history?',
  clearBody:
    '{count, plural, one {# step} other {# steps}} will no longer be undoable. The chart itself does not change, and its versions stay available.',
  clearConfirm: 'Clear history',
  cleared: 'Undo history cleared.',

  // ---- help ----
  helpTitle: 'Undo, history and versions',
  helpBody:
    'Everything you change can be undone and redone: use **Undo** and **Redo** in the top bar or the Edit menu (their shortcuts are shown next to them and in Settings > Keyboard). The History tab lists every step: click one to go back or forward to it. The history survives a reload (the last 500 changes).\n\nYour work is saved automatically as you edit. **Create checkpoint** (in the project menu, with its shortcut next to it) adds a checkpoint now, and **Name it** in the message that follows gives it a name. **Version history** keeps automatic versions, checkpoints and exports: restore one (a single undoable step), open it as a separate chart, or name the version that is the current state.',
});

export default catalogOf(m);
